import { useEffect, useMemo } from 'react';
import { useAccountInfo } from '../api/queryHooks/account';
import { useUserData } from '../api/queryHooks/user';
import { ALL_LOCALES, isLocale, type Locale } from '../i18n/locales';
import { readCachedShopEnabled, writeCachedShopEnabled } from '../i18n/localePreference';

const narrow = (raw: unknown): readonly Locale[] | null => {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const claimed = raw.filter(isLocale);
  if (claimed.length === 0) return null;
  return ALL_LOCALES.filter((l) => claimed.includes(l));
};

/**
 * The locales this shop offers. /users/me carries them for anyone with a
 * session (guest rows included); /accounts/info is the public, origin-resolved
 * fallback, which is what an anonymous visitor has before redeeming an album
 * code. Once a real set is known it's cached (readCachedShopEnabled /
 * writeCachedShopEnabled in localePreference.ts) and used as the fallback
 * BEFORE ALL_LOCALES — both source queries key on i18n.language internally,
 * so a language change transiently drops `data` back to undefined for the
 * new key; falling back to ALL_LOCALES in that gap would let a candidate the
 * shop doesn't actually offer look "offered" again, flip the resolved
 * language back, and re-trigger the same gap — an oscillation that (absent
 * this cache) only self-damps once every visited language's query key has
 * been fetched at least once. ALL_LOCALES is the fallback only when nothing
 * — live or cached — is known yet (e.g. the very first paint ever).
 *
 * Memoized on the raw `enabled_locales` array references (stable across
 * re-renders as long as the underlying query data hasn't changed): `narrow`
 * always allocates a fresh array, and LocaleProvider depends on this result
 * inside a useEffect dependency array, so an unmemoized new-array-every-call
 * would re-run that effect (and its `i18n.changeLanguage` side effect) on
 * every render, not just when the offered set actually changes.
 */
export function useEnabledLocales(): readonly Locale[] {
  const { data: user } = useUserData();
  const { data: account } = useAccountInfo();
  const userLocales = user?.enabled_locales;
  const accountLocales = account?.enabled_locales;

  const real = useMemo(
    () => narrow(userLocales) ?? narrow(accountLocales),
    [userLocales, accountLocales],
  );

  useEffect(() => {
    if (real) writeCachedShopEnabled(real);
  }, [real]);

  return useMemo(
    () => real ?? readCachedShopEnabled() ?? ALL_LOCALES,
    [real],
  );
}
